import React, { useEffect, useRef, useState } from "react";

const EXPORT_FORMATS = [
  { key: "xlsx", label: "Excel (.xlsx)" },
  { key: "pdf", label: "PDF (.pdf)" },
  { key: "png", label: "PNG image (.png)" },
  { key: "jpeg", label: "JPEG image (.jpg)" },
];

const MONTH_NAMES = [
  "January","February","March","April","May","June",
  "July","August","September","October","November","December",
];

export default function RosterHeader({
  year,
  month,
  isDraftMode,
  isCopying,
  isApplying,
  isExporting,
  applyProgress,
  canGoPrev,
  canGoNext,
  onPrev,
  onNext,
  onToday,
  onCopyLastMonth,
  onApplyDraft,
  onDiscardDraft,
  onExport,
  density,
  onDensityToggle,
}) {
  const [exportOpen, setExportOpen] = useState(false);
  const exportRef = useRef(null);

  useEffect(() => {
    if (!exportOpen) return;
    function onDocClick(e) {
      if (!exportRef.current?.contains(e.target)) setExportOpen(false);
    }
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [exportOpen]);

  const lastDate = new Date(year, month - 2, 1);
  const lastLabel = `${MONTH_NAMES[lastDate.getMonth()]} ${lastDate.getFullYear()}`;

  return (
    <div className="roster-header">
      <div className="roster-header-left">
        <button className="btn-secondary btn-xs" onClick={onPrev} disabled={!canGoPrev}>←</button>
        <span className="roster-month-label">{MONTH_NAMES[month - 1]} {year}</span>
        <button className="btn-secondary btn-xs" onClick={onNext} disabled={!canGoNext}>→</button>
        <button className="btn-secondary btn-xs" onClick={onToday}>Today</button>
      </div>

      <div className="roster-header-right">
        <button
          className="btn-secondary btn-xs"
          onClick={onDensityToggle}
          title={density === "compact" ? "Switch to comfortable" : "Switch to compact"}
        >
          {density === "compact" ? "▤ Comfortable" : "▣ Compact"}
        </button>

        <div className="roster-export" ref={exportRef}>
          <button
            className="btn-secondary btn-xs"
            onClick={() => setExportOpen((v) => !v)}
            disabled={isExporting}
            title={`Download ${MONTH_NAMES[month - 1]} ${year} roster`}
            aria-haspopup="menu"
            aria-expanded={exportOpen}
          >
            {isExporting ? "Exporting…" : "Download ▾"}
          </button>
          {exportOpen && (
            <div className="roster-export-menu" role="menu">
              {EXPORT_FORMATS.map((f) => (
                <button
                  key={f.key}
                  role="menuitem"
                  className="roster-export-item"
                  onClick={() => { setExportOpen(false); onExport(f.key); }}
                >
                  {f.label}
                </button>
              ))}
            </div>
          )}
        </div>

        {!isDraftMode && (
          <button
            className="btn-secondary btn-xs"
            onClick={onCopyLastMonth}
            disabled={isCopying}
            title={`Copy ${lastLabel} pattern to this month as a draft`}
          >
            {isCopying ? "Copying…" : `Copy ${lastLabel} →`}
          </button>
        )}

        {isDraftMode && (
          <div className="roster-draft-controls">
            <span className="roster-draft-badge">Draft — unsaved</span>
            {isApplying ? (
              <div className="roster-apply-progress">
                <div className="roster-apply-bar" style={{ width: `${applyProgress}%` }} />
                <span>{applyProgress}%</span>
              </div>
            ) : (
              <>
                <button className="btn-primary btn-xs" onClick={onApplyDraft}>
                  Apply draft
                </button>
                <button className="btn-secondary btn-xs" onClick={onDiscardDraft}>
                  Discard
                </button>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
